import { getFirstHtmlElement } from '@shared/utils/html';
import { HotelOfferData, OfferCancellationPolicyRate, TelHotelSearchMetaData, TelOfferMetaData, TravelOffer, TravelOfferUnitComponent } from '../../HotelPDPPageV2.types';
import { StaysSearchTerms } from '@apps/stays/types';
import { TravelOfferRefundability, TravelOffersForAccommodationPropertiesQuery } from '@graphql/generated';
import { intervalToDuration } from 'date-fns';

/**
 * Enum defining the available strategies for matching hotel offers when merging cancellation policies.
 * This enum should match the values defined in the feature flag configuration.
 */
export enum OfferMatchStrategy {
  /** Default: Match offers by component unit ID */
  Control = 'control',
  /** Match offers by shared offerGroupId */
  OfferGroupId = 'offergroupid',
  /** Match offers by component unit name */
  UnitName = 'unitname',
  /** Match offers by both offerGroupId and unit name */
  GroupIdAndUnitName = 'groupidandunitname',
}

interface OfferWithCancellationPolicyProps
  extends Readonly<{
    offerMatchStrategyValue?: unknown;
    someOffers?: TravelOffersForAccommodationPropertiesQuery['travelOffersForAccommodationProperties']['someOffers'];
  }> {}

type IndexedComponent = Readonly<{
  offer: TravelOffer;
  offerIndex: number;
  componentIndex: number;
  component: TravelOfferUnitComponent;
}>;

/**
 * Converts the feature flag value to a valid OfferMatchStrategy.
 * Defaults to Control for any unrecognized values.
 */
const parseOfferMatchStrategy = (value: unknown): OfferMatchStrategy => {
  switch (value) {
    case OfferMatchStrategy.UnitName:
    case OfferMatchStrategy.OfferGroupId:
    case OfferMatchStrategy.GroupIdAndUnitName:
      return value;
    default:
      return OfferMatchStrategy.Control;
  }
};

// To transform offer/component/unit data from API to presentational offer object
export const transformOfferToHotelOffer = (offer: TravelOffer, numberOfNights: number): HotelOfferData => {
  const offerComponent = offer.components?.[0];
  const offerUnit = offerComponent.unit;
  const photos = offerUnit.photos.map(photo => ({ src: photo.url, alt: photo.url }));
  const description = getFirstHtmlElement(offerUnit.plainTextLongDescription, 'strong') ?? undefined;
  const featuredAmenities: string[] = [];

  if (offerUnit.facilities.some(facility => facility.label.toLowerCase().includes('parking'))) {
    featuredAmenities.push('Parking');
  }

  if (offerUnit.facilities.some(facility => facility.label.toLowerCase().includes('air conditioning'))) {
    featuredAmenities.push('Air conditioning');
  }

  if (offerUnit.facilities.some(facility => facility.label.toLowerCase().includes('private bathroom'))) {
    featuredAmenities.push('Private bathroom');
  }

  if (offerUnit.facilities.some(facility => facility.label.toLowerCase().includes('wifi'))) {
    featuredAmenities.push('Free WiFi');
  }

  if (offerUnit.facilities.some(facility => facility.label.toLowerCase().includes('tv'))) {
    featuredAmenities.push('TV');
  }

  const { numberOfAdults, numberOfChildren } = offerComponent.occupancies.reduce(
    (acc, occupations) => {
      acc.numberOfAdults += occupations.adults;
      acc.numberOfChildren += occupations.children;
      return acc;
    },
    { numberOfAdults: 0, numberOfChildren: 0 },
  );

  const hotelOffer: HotelOfferData = {
    id: offerUnit.id,
    name: offerUnit.name,
    offerToken: offer.offerToken,
    description,
    photos,
    featuredAmenities,
    baseAmount: offer.baseAmount,
    originalBaseAmount: offer.originalBaseAmount,
    totalAmount: offer.totalAmount,
    originalTotalAmount: offer.originalTotalAmount,
    facilities: offerUnit.facilities,
    numberOfAdults,
    numberOfChildren,
    distributionType: offerComponent.distributionType,
    cancellationPolicyRates: offerComponent.cancellationPolicyRates,
    refundability: offerComponent.refundability,
    boardBasis: offerComponent.boardBasis?.label,
    policies: offerComponent.policies,
    numberOfRooms: offerComponent.occupancies.length,
    numberOfNights,
    providerName: offer.providerName,
  };

  return hotelOffer;
};

// To transform offer to offer metadata for telemetry events
export const transformHotelOfferToMetadata = (offer: HotelOfferData): TelOfferMetaData => {
  return {
    offerToken: offer.offerToken,
    baseAmount: offer.baseAmount.floatingPointDecimalString,
    totalAmount: offer.totalAmount.floatingPointDecimalString,
    roomId: offer.id,
    offerName: offer.name,
    numberOfChildren: offer.numberOfChildren,
    numberOfAdults: offer.numberOfAdults,
    refundability: offer.refundability,
    boardBasis: offer.boardBasis,
    numberOfRooms: offer.numberOfRooms,
  };
};

// To transform search params from uil to search metadata for telemetry events
export const transformSearchParamToSearchMetadata = (values: Partial<StaysSearchTerms>): TelHotelSearchMetaData => {
  const { rooms = [], startDate, endDate } = values;

  const { roomCount, adultCount, childCount } = rooms.reduce(
    (acc, room) => {
      acc.roomCount += 1;
      acc.adultCount += room.adults || 0;
      acc.childCount += room.childrenAges?.length || 0;
      return acc;
    },
    { roomCount: 0, adultCount: 0, childCount: 0 },
  );

  const startDateStr = startDate ? `${startDate.year}-${startDate.month}-${startDate.day}` : '';
  const endDateStr = endDate ? `${endDate.year}-${endDate.month}-${endDate.day}` : '';

  return {
    startDate: startDateStr,
    endDate: endDateStr,
    roomCount,
    adultCount,
    childCount,
  };
};

export const transformToReadableRefundability = (refundability: TravelOfferRefundability) => {
  return refundability.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const transformRateToPerNight = (baseAmount: number, numberOfNights: number, numberOfRooms: number): number => {
  const ratePerNight = baseAmount / numberOfNights / numberOfRooms;
  return ratePerNight;
};

export const calculateNumberOfNights = (startDate?: { year: number; month: number; day: number }, endDate?: { year: number; month: number; day: number }) => {
  if (!startDate || !endDate) return 1;

  const start = new Date(startDate.year, startDate.month - 1, startDate.day);
  const end = new Date(endDate.year, endDate.month - 1, endDate.day);

  return Math.max(intervalToDuration({ start, end }).days || 1, 1);
};

export const mergeOffersWithCancellationPolicy = ({ someOffers, offerMatchStrategyValue }: OfferWithCancellationPolicyProps): TravelOffer[] => {
  const offers: TravelOffer[] =
    someOffers?.map(offerQuery => {
      const components: TravelOfferUnitComponent[] = offerQuery.components.map(componentQuery => ({
        ...componentQuery,
        cancellationPolicyRates: [],
      }));
      return { ...offerQuery, components };
    }) ?? [];

  if (!offers.length) {
    return [];
  }

  const matchStrategy = parseOfferMatchStrategy(offerMatchStrategyValue);
  const enableUnitNameMatch = [OfferMatchStrategy.UnitName, OfferMatchStrategy.GroupIdAndUnitName].includes(matchStrategy);
  const enableGroupIdMatch = [OfferMatchStrategy.OfferGroupId, OfferMatchStrategy.GroupIdAndUnitName].includes(matchStrategy);

  const componentsByGroupId = enableGroupIdMatch ? new Map<string, IndexedComponent[]>() : undefined;
  const componentsByUnitId = !enableGroupIdMatch ? new Map<string, IndexedComponent[]>() : undefined;
  const componentsByUnitName = enableUnitNameMatch ? new Map<string, IndexedComponent[]>() : undefined;

  offers.forEach((offer, offerIndex) => {
    offer.components.forEach((component, componentIndex) => {
      const indexedComponent: IndexedComponent = {
        offer,
        component,
        offerIndex,
        componentIndex,
      };

      if (componentsByGroupId && offer.offerGroupId) {
        const existing = componentsByGroupId.get(offer.offerGroupId) ?? [];
        existing.push(indexedComponent);
        componentsByGroupId.set(offer.offerGroupId, existing);
      }

      if (componentsByUnitId) {
        const unitId = component.unit.id;
        if (unitId) {
          const existing = componentsByUnitId.get(unitId) ?? [];
          existing.push(indexedComponent);
          componentsByUnitId.set(unitId, existing);
        }
      }

      if (componentsByUnitName) {
        const unitName = component.unit.name.toLowerCase();
        const existing = componentsByUnitName.get(unitName) ?? [];
        existing.push(indexedComponent);
        componentsByUnitName.set(unitName, existing);
      }
    });
  });

  const processedOfferTokens: Set<string> = new Set();
  const mergedOfferTokens: Set<string> = new Set();
  const processedOffers: TravelOffer[] = [];

  offers.forEach(offer => {
    const matchingOffersByComponentId = new Map<string, OfferCancellationPolicyRate[]>();

    offer.components.forEach(component => {
      const componentId = enableGroupIdMatch ? (offer.offerGroupId ?? undefined) : (component.unit.id ?? undefined);

      if (!componentId) {
        return;
      }

      const existingRates = matchingOffersByComponentId.get(componentId);
      const cancellationRates = existingRates ?? [];
      if (!existingRates) {
        matchingOffersByComponentId.set(componentId, cancellationRates);
      }

      const candidateComponents: IndexedComponent[] = [];
      const seenCandidateKeys = new Set<string>();
      const addCandidates = (candidates?: IndexedComponent[]) => {
        candidates?.forEach(candidate => {
          const candidateKey = `${candidate.offerIndex}:${candidate.componentIndex}`;
          if (!seenCandidateKeys.has(candidateKey)) {
            seenCandidateKeys.add(candidateKey);
            candidateComponents.push(candidate);
          }
        });
      };

      if (enableGroupIdMatch) {
        if (offer.offerGroupId && componentsByGroupId) {
          addCandidates(componentsByGroupId.get(offer.offerGroupId));
        }
      } else {
        if (componentsByUnitId) {
          addCandidates(componentsByUnitId.get(component.unit.id));
        }
      }

      if (enableUnitNameMatch && componentsByUnitName) {
        const unitName = component.unit.name.toLowerCase();
        if (unitName) {
          addCandidates(componentsByUnitName.get(unitName));
        }
      }

      candidateComponents.forEach(({ offer: candidateOffer, component: candidateComponent }) => {
        const candidateToken = candidateOffer.offerToken;
        if (processedOfferTokens.has(candidateToken)) {
          return;
        }

        const currentUnitName = component.unit.name?.toLowerCase();
        const candidateUnitName = candidateComponent.unit.name?.toLowerCase();

        const isMatchByIdentifier = enableGroupIdMatch ? candidateOffer.offerGroupId === componentId : candidateComponent.unit.id === componentId;
        const isMatchByOfferToken = candidateToken === offer.offerToken && offer.offerToken !== '';
        const isMatchByUnitName = enableUnitNameMatch && candidateUnitName && currentUnitName === candidateUnitName;

        if (!(isMatchByIdentifier || isMatchByOfferToken || isMatchByUnitName)) {
          return;
        }

        const insertIndex = cancellationRates.findIndex(rate => rate.baseAmount.valueInMinorUnits > candidateOffer.baseAmount.valueInMinorUnits);

        const newRate: OfferCancellationPolicyRate = {
          offerToken: candidateToken,
          policies: candidateComponent.policies,
          baseAmount: candidateOffer.baseAmount,
          totalAmount: candidateOffer.totalAmount,
          refundability: candidateComponent.refundability,
          boardBasis: candidateComponent.boardBasis?.label,
          originalBaseAmount: candidateOffer.originalBaseAmount,
        };

        if (insertIndex === -1) {
          cancellationRates.push(newRate);
        } else {
          cancellationRates.splice(insertIndex, 0, newRate);
        }

        processedOfferTokens.add(candidateToken);

        if (candidateToken !== offer.offerToken) {
          mergedOfferTokens.add(candidateToken);
        }
      });

      if (cancellationRates.length) {
        component.cancellationPolicyRates = cancellationRates;
      }
    });

    processedOffers.push(offer);
  });

  return processedOffers
    .filter(offer => !mergedOfferTokens.has(offer.offerToken) && offer.baseAmount)
    .sort((a, b) => a.totalAmount.valueInMinorUnits - b.totalAmount.valueInMinorUnits);
};
